import { authService, profileService } from "@b2b/services";
import type { UserStatus } from "@b2b/services";

export type HeaderUserSummary = {
  name: string;
  role: string | null;
  profileImage: string | null;
  /** From GET /me — gate dashboard / messages in marketing header until registration is complete. */
  meStatus: UserStatus;
  /** For redirect to signup when `meStatus` is not `active`. */
  prefillEmail: string | null;
};

const ACCESS_TOKEN_KEY = "bt_b2b_access_token";
const CACHE_TTL_MS = 5 * 60 * 1000;

let cachedHeaderUser: HeaderUserSummary | null = null;
let cachedAt = 0;
let inflightRequest: Promise<HeaderUserSummary | null> | null = null;

function hasAccessToken(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(window.localStorage.getItem(ACCESS_TOKEN_KEY));
}

function getRoleFromUser(rawRole: string | undefined): string | null {
  const role = String(rawRole || "").toLowerCase();
  return role || null;
}

async function resolveProfileImage(
  role: string | null,
  user: { profileImage?: string; avatar?: string } | undefined
): Promise<string | null> {
  const meImage = String(user?.profileImage || user?.avatar || "").trim();
  if (meImage) return meImage;

  try {
    const response = await profileService.getProfile();
    const raw = response as unknown as Record<string, unknown>;
    const profile =
      raw?.success === true && raw?.data
        ? (raw.data as Record<string, unknown>)
        : raw;
    const company = (profile?.company ?? {}) as Record<string, unknown>;
    const isSeller = role === "seller" || role === "buyer_seller";
    const sellerLogo = String(
      company.logo ?? profile?.companyLogo ?? profile?.logo ?? ""
    ).trim();
    const buyerImage = String(profile?.profileImage ?? "").trim();
    return (isSeller ? sellerLogo : buyerImage) || null;
  } catch {
    return null;
  }
}

export function invalidateHeaderUserCache(): void {
  cachedHeaderUser = null;
  cachedAt = 0;
  inflightRequest = null;
}

export async function getCachedHeaderUser(
  options?: { force?: boolean }
): Promise<HeaderUserSummary | null> {
  const force = options?.force === true;

  if (!hasAccessToken()) {
    invalidateHeaderUserCache();
    return null;
  }

  if (!force && cachedHeaderUser && Date.now() - cachedAt < CACHE_TTL_MS) {
    return cachedHeaderUser;
  }

  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    const meResponse = await authService.getMe();
    const user = meResponse?.user;
    const meStatus = (meResponse?.status ?? "active") as UserStatus;
    const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    const fallbackName = user?.email?.split("@")[0] || "Account";
    const role = getRoleFromUser(user?.role);
    const profileImage = await resolveProfileImage(role, user);
    const prefillEmail = String(user?.email || "").trim() || null;

    const summary: HeaderUserSummary = {
      name: fullName || fallbackName,
      role,
      profileImage,
      meStatus,
      prefillEmail,
    };

    cachedHeaderUser = summary;
    cachedAt = Date.now();
    return summary;
  })()
    .catch((error) => {
      invalidateHeaderUserCache();
      throw error;
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
}
